import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Tag, TrendingUp, Calendar, Mail, Archive, Loader2, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BlogSidebarProps {
  searchQuery?: string;
  setSearchQuery?: (query: string) => void;
  selectedCategory?: string;
  setSelectedCategory?: (category: string) => void;
  categories?: string[];
  posts?: any[];
}

export const BlogSidebar: React.FC<BlogSidebarProps> = ({
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  categories = [],
  posts = [],
}) => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubscribe = async () => {
    const email = newsletterEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setIsSubscribing(true);
    try {
      // Check if already subscribed
      const { data: existing } = await supabase
        .from('status_subscriptions')
        .select('id, unsubscribed_at')
        .eq('email', email)
        .maybeSingle();

      if (existing && !existing.unsubscribed_at) {
        toast.info('You are already subscribed!');
        setIsSubscribed(true);
        return;
      }

      if (existing && existing.unsubscribed_at) {
        // Re-subscribe
        await supabase
          .from('status_subscriptions')
          .update({ unsubscribed_at: null, subscribed_at: new Date().toISOString() })
          .eq('id', existing.id);
      } else {
        // New subscriber
        const { error } = await supabase
          .from('status_subscriptions')
          .insert({ email, subscribed_at: new Date().toISOString() });
        if (error) throw error;
      }

      setIsSubscribed(true);
      setNewsletterEmail('');
      toast.success('Successfully subscribed to the newsletter!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <aside className="w-full lg:w-80 space-y-8 shrink-0">
      <div className="bg-white dark:bg-zinc-900 p-6 rounded-[2rem] border border-zinc-200 dark:border-zinc-800 shadow-sm">
        <h3 className="text-lg font-black mb-4 flex items-center gap-2 uppercase tracking-tight">
          <Search className="w-5 h-5 text-primary" /> Search
        </h3>
        <div className="relative">
          <Input 
            placeholder="Search posts..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery?.(e.target.value)} 
            className="pl-10 h-12 bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 rounded-2xl focus-visible:ring-primary" 
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        </div>
      </div>

      {categories.length > 0 && (
        <div className="bg-white dark:bg-zinc-900 p-6 rounded-[2rem] border border-zinc-200 dark:border-zinc-800 shadow-sm">
          <h3 className="text-lg font-black mb-4 flex items-center gap-2 uppercase tracking-tight">
            <Tag className="w-5 h-5 text-primary" /> Categories
          </h3>
          <div className="space-y-1">
            {categories.map((cat) => (
              <button 
                key={cat} 
                onClick={() => setSelectedCategory?.(cat)} 
                className={cn(
                  "w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-bold transition-all", 
                  selectedCategory === cat 
                    ? "bg-primary text-white shadow-lg shadow-primary/20" 
                    : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                )}
              >
                {cat}
                <span className={cn(
                  "text-[10px] px-2 py-0.5 rounded-full",
                  selectedCategory === cat ? "bg-white/20" : "bg-zinc-100 dark:bg-zinc-800"
                )}>
                  {cat === 'All' ? posts.length : posts.filter(p => p.tags?.includes(cat)).length}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-zinc-900 p-6 rounded-[2rem] border border-zinc-200 dark:border-zinc-800 shadow-sm">
        <h3 className="text-lg font-black mb-4 flex items-center gap-2 uppercase tracking-tight">
          <TrendingUp className="w-5 h-5 text-primary" /> Trending Now
        </h3>
        <div className="space-y-6">
          {posts.length === 0 && (
            <p className="text-sm text-zinc-400">No posts yet.</p>
          )}
          {posts.slice(0, 3).map((post) => (
            <Link key={post.slug} to={`/blog/${post.slug}`} className="group flex gap-4">
              <div className="w-16 h-16 rounded-2xl overflow-hidden shrink-0 border border-zinc-100 dark:border-zinc-800 shadow-sm bg-zinc-100 dark:bg-zinc-800">
                {post.image_url ? (
                  <img 
                    src={post.image_url} 
                    alt={post.title} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-primary font-black text-lg">
                    {post.title?.[0] || 'Q'}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-black leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                  {post.title}
                </h4>
                <div className="flex items-center gap-2 text-[10px] text-zinc-400 font-bold uppercase tracking-wider">
                  <Calendar className="w-3 h-3" /> {post.published_at ? new Date(post.published_at).toLocaleDateString() : post.created_at ? new Date(post.created_at).toLocaleDateString() : ''}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="bg-gradient-to-br from-primary to-accent p-8 rounded-[2rem] text-white shadow-xl shadow-primary/20 overflow-hidden relative group">
        <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-white/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
        <h3 className="text-xl font-black mb-2 flex items-center gap-2">
          <Mail className="w-5 h-5" /> Newsletter
        </h3>
        <p className="text-sm text-white/80 mb-6 font-medium leading-relaxed">
          Join 50k+ readers. Get the latest QR trends delivered to your inbox.
        </p>
        {isSubscribed ? (
          <div className="flex items-center gap-2 bg-white/10 rounded-xl p-4">
            <CheckCircle className="w-5 h-5 text-green-300 shrink-0" />
            <p className="text-sm font-bold">You're subscribed! We'll keep you updated.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Input 
              placeholder="Your email address"
              type="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubscribe()}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-xl h-11 focus-visible:ring-white" 
              disabled={isSubscribing}
            />
            <Button 
              className="w-full bg-white text-primary hover:bg-zinc-100 font-black rounded-xl h-11"
              onClick={handleSubscribe}
              disabled={isSubscribing}
            >
              {isSubscribing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subscribing...</>
              ) : (
                'Subscribe Now'
              )}
            </Button>
          </div>
        )}
      </div>

      <div className="bg-zinc-100 dark:bg-zinc-800/50 p-6 rounded-[2rem] border-2 border-dashed border-zinc-200 dark:border-zinc-700 flex flex-col items-center justify-center text-center py-12">
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 mb-2">Advertisement</span>
        <div className="text-sm font-bold text-zinc-500 dark:text-zinc-400">
          Space Available for <br />
          <span className="text-primary">QR Solutions</span>
        </div>
      </div>
    </aside>
  );
};
